/**
 * Mobile Medication Logging Page
 * Allows recording medication administration offline with auto-sync
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { fetchAllPages } from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Pill, Clock, Check, X, AlertTriangle, RefreshCw, Ban, Sparkles, ShieldAlert, ClipboardCheck, UserCheck } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

const MED_STATUSES = [
  { value: 'ADMINISTERED', label: 'Administered', icon: Check, color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  { value: 'REFUSED', label: 'Refused', icon: X, color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { value: 'MISSED', label: 'Missed', icon: AlertTriangle, color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  { value: 'WITHHELD', label: 'Withheld', icon: Ban, color: 'bg-purple-500/10 text-purple-600 border-purple-500/30' },
  { value: 'PRN_GIVEN', label: 'PRN Given', icon: Sparkles, color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
];

// Outcomes that require a clinical reason (mirrors backend MED_REASON_REQUIRED)
const REASON_REQUIRED = new Set(['REFUSED', 'MISSED', 'WITHHELD', 'PRN_GIVEN']);

// Stable per-device identifier for medication audit metadata
function getDeviceId() {
  let id = localStorage.getItem('gc360_device_id');
  if (!id) {
    id = `web-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    localStorage.setItem('gc360_device_id', id);
  }
  return id;
}

export default function MobileMedicationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const medicationId = searchParams.get('medicationId');
  const scheduleId = searchParams.get('scheduleId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [medications, setMedications] = useState([]);
  const [dueMedications, setDueMedications] = useState([]);
  const [dueGroups, setDueGroups] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [allowOverride, setAllowOverride] = useState(false);
  const [dueReviews, setDueReviews] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);
  const [pendingSecondSign, setPendingSecondSign] = useState([]);
  const [signingId, setSigningId] = useState(null);
  const [form, setForm] = useState({
    participantId: participantId || '',
    medicationId: medicationId || '',
    scheduleId: scheduleId || '',
    dose: '',
    timeAdministered: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
    status: 'ADMINISTERED',
    reason: '',
    prnReviewMinutes: 60,
    notes: '',
  });

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants
  useEffect(() => {
    if (online) {
      fetchAllPages('/participants')   // NEW-007: every assigned participant, not just the first 100
        .then(items => setParticipants(items || []))
        .catch(() => {});
    }
  }, [online]);

  // Fetch medications when participant changes
  useEffect(() => {
    if (online && form.participantId) {
      // Fetch participant's medications using correct endpoint
      api.get(`/medications/by-participant/${form.participantId}`, { params: { pageSize: 100 } })
        .then(res => {
          setMedications(res.data.items || res.data.data || []);
        })
        .catch(() => {});
      
      // Fetch due medications
      api.get('/mobile/medications/due', { params: { participantId: form.participantId } })
        .then(res => {
          setDueMedications(res.data.data || []);
          setDueGroups(res.data.groups || []);
        })
        .catch((err) => {
          if (err.response?.status === 403) setAccessDenied(true);
        });
    }
  }, [online, form.participantId]);

  // Update selected medication details
  useEffect(() => {
    if (form.medicationId) {
      const med = medications.find(m => m.id === form.medicationId);
      if (med) {
        setSelectedMedication(med);
        if (med.prn && form.status === 'ADMINISTERED') {
          setForm(prev => ({ ...prev, status: 'PRN_GIVEN' }));
        }
        if (!form.dose) {
          setForm(prev => ({ ...prev, dose: med.dosage || '' }));
        }
      }
    }
  }, [form.medicationId, medications]);

  // Day 34 — run a safety check when medication + participant + time are set
  useEffect(() => {
    setAllowOverride(false);
    if (online && form.medicationId && form.participantId) {
      api.post('/mobile/medications/safety-check', {
        medicationId: form.medicationId,
        participantId: form.participantId,
        timeAdministered: form.timeAdministered,
      })
        .then(res => {
          setWarnings(res.data?.data?.warnings || []);
          setDuplicateInfo(res.data?.data?.duplicate || null);
        })
        .catch(() => { setWarnings([]); setDuplicateInfo(null); });
    } else {
      setWarnings([]); setDuplicateInfo(null);
    }
  }, [online, form.medicationId, form.participantId, form.timeAdministered]);

  // Day 33 — load PRN effectiveness reviews due for this worker
  const loadDueReviews = useCallback(() => {
    if (!online) return;
    api.get('/mobile/medications/prn-reviews/due')
      .then(res => setDueReviews(res.data?.data || []))
      .catch(() => {});
  }, [online]);

  useEffect(() => { loadDueReviews(); }, [loadDueReviews]);

  // Day 35 — load high-risk medications awaiting a second signature
  const loadPendingSecondSign = useCallback(() => {
    if (!online) return;
    api.get('/mobile/medications/pending-second-sign')
      .then(res => setPendingSecondSign(res.data?.data || []))
      .catch(() => {});
  }, [online]);

  useEffect(() => { loadPendingSecondSign(); }, [loadPendingSecondSign]);

  const coSign = async (adminId) => {
    setSigningId(adminId);
    try {
      await api.post(`/mobile/medications/${adminId}/second-sign`);
      toast.success('Second signature recorded — administration complete');
      loadPendingSecondSign();
    } catch (err) {
      const code = err.response?.data?.error?.code || err.response?.data?.detail?.code;
      toast.error(code === 'SAME_SIGNER'
        ? 'A different staff member must provide the second signature'
        : 'Could not record second signature');
    } finally {
      setSigningId(null);
    }
  };

  const submitPrnReview = async (reviewId, effective) => {
    setReviewingId(reviewId);
    try {
      await api.post(`/mobile/medications/${reviewId}/prn-review`, {
        effective, followUpRequired: !effective,
      });
      toast.success(effective ? 'Marked effective' : 'Marked not effective — follow up flagged');
      loadDueReviews();
    } catch (err) {
      toast.error('Could not save review');
    } finally {
      setReviewingId(null);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const selectDueMedication = (dueMed) => {
    setForm(prev => ({
      ...prev,
      medicationId: dueMed.medicationId,
      scheduleId: dueMed.scheduleId,
      participantId: dueMed.participantId,
      dose: dueMed.dose,
    }));
  };

  const saveDraft = async (override = false) => {
    if (!form.medicationId) {
      toast.error('Please select a medication');
      return;
    }
    if (!form.participantId) {
      toast.error('Please select a participant');
      return;
    }
    if (!form.status) {
      toast.error('Please select a status');
      return;
    }
    // Day 32 — reason required for refused/missed/withheld/PRN
    if (REASON_REQUIRED.has(form.status) && !form.reason.trim()) {
      toast.error(`A reason is required when a medication is ${form.status.replace('_', ' ').toLowerCase()}`);
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      await queueOfflineAction(ACTION_TYPES.MEDICATION_LOG, {
        medicationId: form.medicationId,
        participantId: form.participantId,
        scheduleId: form.scheduleId || null,
        dose: form.dose,
        timeAdministered: form.timeAdministered,
        status: form.status,
        reason: form.reason.trim() || null,
        notes: form.notes,
        deviceId: getDeviceId(),
        prnReviewMinutes: form.status === 'PRN_GIVEN' ? Number(form.prnReviewMinutes) || 60 : undefined,
        overrideDuplicate: override,
        timestamp: new Date().toISOString(),
      });

      toast.success('Medication record saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.forbidden > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            setAccessDenied(true);
          } else if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Medication record synced');
            loadDueReviews();
            loadPendingSecondSign();
            setTimeout(() => navigate(-1), 1000);
          } else if (results.conflicts > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            setAllowOverride(true);
            toast.error('Medication already recorded', {
              description: 'Use "Administer anyway" to record an authorised override.',
            });
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            toast.error('Could not save', { description: 'Check the outcome and reason, then retry.' });
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save medication record');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <>
    {accessDenied && <MobileAccessDenied />}
    <div className={`min-h-screen bg-background pb-24 ${accessDenied ? 'hidden' : ''}`} data-testid="mobile-medication-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/80" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              <h1 className="text-lg font-bold">Record Medication</h1>
            </div>
            <p className="text-xs opacity-80">Log medication administration</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Day 33 — PRN effectiveness reviews due */}
      {dueReviews.length > 0 && (
        <div className="px-4 pt-4" data-testid="prn-reviews-due">
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-blue-600" />
                PRN reviews due ({dueReviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {dueReviews.map(r => (
                <div key={r.id} className="rounded-lg border bg-background p-3" data-testid={`prn-review-${r.id}`}>
                  <p className="text-sm font-medium">{r.medicationName}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.participantName || 'Participant'} · given {r.administeredAt?.slice(11, 16)}
                    {r.isOverdue && <span className="ml-1 text-red-600 font-medium">· review overdue</span>}
                  </p>
                  {r.reason && <p className="text-[11px] text-muted-foreground mt-0.5">Reason: {r.reason}</p>}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="flex-1 h-8 text-xs"
                      disabled={reviewingId === r.id}
                      onClick={() => submitPrnReview(r.id, true)}
                      data-testid={`prn-effective-${r.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Effective
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 h-8 text-xs"
                      disabled={reviewingId === r.id}
                      onClick={() => submitPrnReview(r.id, false)}
                      data-testid={`prn-not-effective-${r.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Not effective
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Day 35 — high-risk medications awaiting a second signature */}
      {pendingSecondSign.length > 0 && (
        <div className="px-4 pt-4" data-testid="pending-second-sign">
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-purple-600" />
                Awaiting second signature ({pendingSecondSign.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {pendingSecondSign.map(r => (
                <div key={r.id} className="rounded-lg border bg-background p-3" data-testid={`second-sign-${r.id}`}>
                  <p className="text-sm font-medium">{r.medicationName}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.participantName || 'Participant'} · {r.doseGiven || ''} · 1st sign: {r.firstSignerName || 'staff'}
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 h-8 text-xs w-full"
                    variant={r.isOwnEntry ? 'outline' : 'default'}
                    disabled={r.isOwnEntry || signingId === r.id}
                    onClick={() => coSign(r.id)}
                    data-testid={`co-sign-btn-${r.id}`}
                  >
                    <UserCheck className="h-3.5 w-3.5 mr-1" />
                    {r.isOwnEntry ? 'Needs a different staff member' : 'Co-sign as second staff'}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Day 34 — safety warnings for the selected medication */}
      {warnings.length > 0 && (
        <div className="px-4 pt-4 space-y-2" data-testid="med-safety-warnings">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs font-medium ${
                w.severity === 'HIGH'
                  ? 'bg-red-500/10 text-red-700'
                  : w.severity === 'MEDIUM'
                  ? 'bg-amber-500/10 text-amber-700'
                  : 'bg-muted text-muted-foreground'
              }`}
              data-testid={`med-warning-${w.type?.toLowerCase()}`}
            >
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Day 31 — Due Medications v2 (grouped by participant, with safety info) */}
      {dueGroups.length > 0 && (
        <div className="px-4 pt-4 space-y-3" data-testid="due-meds-v2">
          {dueGroups.map(group => (
            <Card key={group.participantId} className="border-amber-500/20 bg-amber-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  Due for {group.participantName || 'participant'}
                </CardTitle>
                {group.allergyWarnings && (
                  <div className="flex items-center gap-1 mt-1 rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-700 font-medium" data-testid="allergy-warning">
                    <AlertTriangle className="h-3.5 w-3.5" /> Allergies: {group.allergyWarnings}
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="space-y-2">
                  {group.medications.map(med => (
                    <button
                      key={med.scheduleId}
                      onClick={() => selectDueMedication(med)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                        form.medicationId === med.medicationId ? 'border-primary bg-primary/5' : 'border-transparent bg-background hover:bg-muted'
                      }`}
                      data-testid={`due-med-${med.medicationId}`}
                    >
                      {med.medicationPhoto ? (
                        <img src={med.medicationPhoto} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center shrink-0"><Pill className="h-5 w-5 text-primary" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{med.medicationName}</p>
                        <p className="text-xs text-muted-foreground">
                          {med.dose}{med.route ? ` · ${med.route}` : ''} · {med.scheduledTime}
                        </p>
                        {med.instructions && <p className="text-[11px] text-muted-foreground truncate">{med.instructions}</p>}
                      </div>
                      <Badge
                        variant={med.dueStatus === 'OVERDUE' ? 'destructive' : 'secondary'}
                        className="text-xs shrink-0"
                        data-testid={`due-status-${med.medicationId}`}
                      >
                        {med.dueStatus === 'OVERDUE' ? `${med.minutesOverdue}min overdue` : 'Due'}
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Participant & Medication Selection */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant *</Label>
              <SearchSelect
                options={participants.map(p => ({ value: p.id, label: participantLabel(p), keywords: p.ndis_number || '' }))}
                value={form.participantId}
                onChange={(v) => handleChange('participantId', v)}
                placeholder="Select participant"
                searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                emptyText="No participants match that search"
                className="h-11"
                testId="participant-select"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="medication">Medication *</Label>
              <Select 
                value={form.medicationId || undefined} 
                onValueChange={(v) => handleChange('medicationId', v)}
                disabled={!form.participantId}
              >
                <SelectTrigger id="medication" data-testid="medication-select">
                  <SelectValue placeholder={form.participantId ? "Select medication" : "Select participant first"} />
                </SelectTrigger>
                <SelectContent>
                  {medications.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} ({m.dosage || 'No dosage'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedMedication && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedMedication.name}</p>
                {selectedMedication.instructions && (
                  <p className="text-xs text-muted-foreground mt-1">{selectedMedication.instructions}</p>
                )}
                {selectedMedication.requires_double_sign && (
                  <Badge variant="outline" className="mt-2 text-xs">Requires Witness</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Administration Status *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-3 gap-2">
              {MED_STATUSES.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('status', value)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    form.status === value 
                      ? `${color} border-current` 
                      : 'bg-muted/50 border-transparent hover:bg-muted'
                  }`}
                  data-testid={`status-${value.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Day 32/33 — Reason (required for non-administered outcomes) + PRN review window */}
        {REASON_REQUIRED.has(form.status) && (
          <Card className="border-amber-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Reason *
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <Textarea
                id="reason"
                placeholder={
                  form.status === 'PRN_GIVEN'
                    ? 'Why was this PRN medication given? (e.g., agitation, pain)'
                    : `Why was this medication ${form.status.replace('_', ' ').toLowerCase()}?`
                }
                value={form.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                rows={2}
                className="resize-none"
                data-testid="reason-input"
              />
              {form.status === 'PRN_GIVEN' && (
                <div className="space-y-2">
                  <Label htmlFor="prnMinutes">Effectiveness review in (minutes)</Label>
                  <Input
                    id="prnMinutes"
                    type="number"
                    min={5}
                    value={form.prnReviewMinutes}
                    onChange={(e) => handleChange('prnReviewMinutes', e.target.value)}
                    data-testid="prn-minutes-input"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    A review task will be created and you and the coordinator notified.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dose & Time */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dose">Dose</Label>
              <Input
                id="dose"
                placeholder="e.g., 10mg, 2 tablets"
                value={form.dose}
                onChange={(e) => handleChange('dose', e.target.value)}
                data-testid="dose-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeAdministered">Time Administered</Label>
              <Input
                id="timeAdministered"
                type="datetime-local"
                value={form.timeAdministered}
                onChange={(e) => handleChange('timeAdministered', e.target.value)}
                data-testid="time-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Notes (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="notes"
              placeholder="Any observations or notes about this administration..."
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="notes-input"
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        {allowOverride && (
          <Button
            variant="destructive"
            className="w-full mb-2"
            onClick={() => saveDraft(true)}
            disabled={saving}
            data-testid="override-duplicate-btn"
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            Administer anyway (authorised override)
          </Button>
        )}
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1"
            onClick={() => saveDraft(false)}
            disabled={saving || !form.medicationId || !form.participantId || !form.status}
            data-testid="save-medication-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {online ? 'Save & Sync' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
